import { FileText, ImageIcon, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useMisCapacidades } from '../../api/capacidades'
import { familiaDe, miniaturaDe, tamano, useMedio, useMedios, type Medio as Doc } from '../../api/medios'
import { BarraDeBusqueda, Boton, Esqueleto, Modal } from '../../componentes/ui'
import { SubidaDeUnFichero } from '../../medios/SubidaPorLotes'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { Envoltura } from './Controles'

export { Modal }

/**
 * Elegir un fichero de la biblioteca, o subir uno nuevo y elegirlo. El campo guarda el id; la
 * miniatura se pide aparte. Nunca una dirección escrita a mano (§8).
 *
 * Subir va **por el mismo camino que la biblioteca** (plan del CMS dinámico §13): un lote de uno,
 * analizado por el núcleo (tipo de verdad, antivirus); se elige cuando queda listo. Solo se ofrece
 * lo que está listo: el núcleo no deja guardar otra cosa. Un campo que admite documentos lo dice
 * el núcleo con `custom.panel.control = 'archivo'` (el bloque «Descarga», un campo de archivo):
 * ahí se ven y se suben documentos, y se puede elegir que sea privado.
 */
export const admiteDocumentos = (campo: Pick<Campo, 'control'>): boolean => campo.control?.control === 'archivo'

export const SelectorDeMedios = ({ sitio, espacio, alElegir, cerrar, documentos = false }: { alElegir: (m: Doc) => void; cerrar: () => void; documentos?: boolean; espacio: number; sitio: number }) => {
  const [busqueda, setBusqueda] = useState('')
  const permisos = useMisCapacidades(espacio)
  const { data, isPending } = useMedios(sitio, busqueda, 1, 40, { soloListos: true, tipo: documentos ? '' : 'imagenes' })
  const [subidoId, setSubidoId] = useState<null | number>(null)
  const { data: subido } = useMedio(subidoId)
  // Lo recién subido se elige en cuanto se puede leer entero (con su dirección).
  useEffect(() => {
    if (subido) alElegir(subido)
  }, [subido, alElegir])
  return (
    <Modal cerrar={cerrar} titulo={documentos ? 'Elegir un fichero' : 'Elegir una imagen'}>
      <div className="grid gap-6 md:grid-cols-[1fr_18rem]">
        <div>
          <BarraDeBusqueda alBuscar={setBusqueda} className="mb-4" placeholder="Buscar por texto alternativo o nombre…" valor={busqueda} />
          {isPending ? (
            <Esqueleto />
          ) : !data?.docs.length ? (
            <p className="rounded-caja border border-dashed border-linea-fuerte p-8 text-center text-sm text-tenue">{documentos ? 'No hay ficheros todavía.' : 'No hay imágenes todavía.'} Sube {documentos ? 'el primero' : 'la primera'} a la derecha.</p>
          ) : (
            <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {data.docs.map((m) => (
                <li key={m.id}>
                  <button
                    className="group block w-full overflow-hidden rounded-caja border border-linea bg-papel-2 text-left transition-shadow hover:border-violeta hover:shadow-media"
                    onClick={() => alElegir(m)}
                    type="button"
                  >
                    <span className="flex aspect-square items-center justify-center overflow-hidden">
                      {familiaDe(m) === 'documento' ? (
                        <FileText aria-hidden className="size-7 text-tenue" />
                      ) : (
                        miniaturaDe(m) && <img alt={m.alt} className="size-full object-cover transition-transform group-hover:scale-105" loading="lazy" src={miniaturaDe(m)!} />
                      )}
                    </span>
                    <span className="block truncate px-2 py-1.5 text-xs text-tinta-2">{m.alt}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="mb-3 text-[13px] font-medium">{documentos ? 'Subir uno nuevo' : 'Subir una nueva'}</p>
          <SubidaDeUnFichero alSubir={setSubidoId} documentos={documentos} espacio={espacio} permitePrivado={permisos.tiene('medios.privados')} sitio={sitio} />
        </div>
      </div>
    </Modal>
  )
}

const idDe = (v: unknown): null | number => (typeof v === 'number' ? v : v && typeof v === 'object' && 'id' in v ? Number((v as { id: unknown }).id) : null)

export const Medio = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<unknown>(ruta)
  const { sitio, espacio } = useFormulario()
  const id = idDe(valor)
  const { data: medio } = useMedio(id)
  const [abierto, setAbierto] = useState(false)
  const miniatura = miniaturaDe(medio)

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex items-center gap-3 rounded-caja border border-linea bg-papel-2/60 p-2.5">
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-linea bg-papel">
          {medio && familiaDe(medio) === 'documento' ? <FileText className="size-5 text-tenue" /> : miniatura ? <img alt={medio?.alt ?? ''} className="size-full object-cover" src={miniatura} /> : <ImageIcon className="size-5 text-tenue" />}
        </div>
        <div className="min-w-0 flex-1">
          {medio ? (
            <p className="truncate text-[13px]">
              {medio.alt} <span className="text-tenue">· {tamano(medio.filesize)}</span>
            </p>
          ) : (
            <p className="text-[13px] text-tenue">{admiteDocumentos(campo) ? 'Sin fichero' : 'Sin imagen'}</p>
          )}
          {!soloLectura && (
            <div className="mt-1.5 flex gap-1.5">
              <Boton onClick={() => setAbierto(true)} pequeno>
                {id ? 'Cambiar' : admiteDocumentos(campo) ? 'Elegir fichero' : 'Elegir imagen'}
              </Boton>
              {id && (
                <Boton icono={<X />} onClick={() => cambiar(null)} pequeno tipo="discreto">
                  Quitar
                </Boton>
              )}
            </div>
          )}
        </div>
      </div>
      {abierto && sitio && (
        <SelectorDeMedios
          alElegir={(m) => {
            cambiar(m.id)
            setAbierto(false)
          }}
          cerrar={() => setAbierto(false)}
          documentos={admiteDocumentos(campo)}
          espacio={espacio}
          sitio={sitio}
        />
      )}
    </Envoltura>
  )
}
